/**
 * The assistant: question in, running computation out.
 *
 * The loop the project description commits to is
 *
 *     student question -> computational experiment -> result in the scene
 *                      -> a sharper question
 *
 * and this class is the machinery of it. It routes a question to an intent,
 * hands that intent a context, and returns the report the intent assembled from
 * real numbers.
 *
 * IT KNOWS NOTHING ABOUT ANY SIMULATION. It was originally typed to EtchSim,
 * which meant the deposition module could not have an assistant at all without
 * a second copy of this file. Now the module supplies a context factory that
 * closes over whatever sim it owns, and the assistant only supplies the
 * progress tracking and the routing. Adding an assistant to the next module is
 * a context factory and a list of intents.
 *
 * SWAPPING IN A HOSTED MODEL. The only local-only piece is `router`, which maps
 * text to an intent by pattern. Replace that one function with a call to Claude
 * that returns a routing and the rest of the pipeline is unchanged - the
 * experiments still run locally against the solver, which is what keeps the
 * answers honest and keeps the thing working when the network does not.
 */

import { t, tOr } from '../i18n/i18n'
import { scanParams, type IntentParams } from './params'
import type {
  BaseIntentContext,
  ExperimentReport,
  Intent,
  TrackRun,
} from './types'

export interface AskProgress {
  /** Conditions completed so far. */
  done: number
  /** Best known total; grows if the intent queues more. */
  total: number
  label: string
}

export interface Transcript {
  role: 'student' | 'assistant'
  text: string
  report?: ExperimentReport
}

/**
 * An assistant seen from outside its own panel: ask a question, get the
 * report the experiment produced.
 *
 * `Assistant<C>` is generic in the context its module's intents run against,
 * and that type parameter is exactly what a caller who only wants to ASK
 * cannot know - a talking technician is not going to name `EtchIntentContext`.
 * Nor should the panel hand out the concrete class: `reset`, `router` and
 * `installRouterFactory` belong to whoever owns the module, not to a passer-by.
 *
 * So a panel exposes this instead (`assistant` in tools/types.ts) and the
 * conversation in src/talk runs experiments through it. Every `Assistant<C>`
 * satisfies it structurally, for every C.
 */
export interface RunnableAssistant {
  ask(question: string, onProgress?: (p: AskProgress) => void): Promise<Transcript>
  suggestions(): Array<{ label: string; question: string; summary: string }>
}

/**
 * What a router decided about a question.
 *
 * It used to be `Intent | null`, and both halves of that were too narrow.
 *
 * `run` carries the knobs the question named, so an experiment can be designed
 * around the student's own number rather than only selected by their topic.
 *
 * `nearest` is the case the old shape could not express at all: the question is
 * about this tool, no experiment answers it, but one is close. That used to
 * collapse into the same flat refusal as a question about the weather. Note
 * that it carries an INTENT and no prose - the sentence the student reads is
 * composed from that intent's own `example` and `summary`, which are authored
 * in this repo. A router never gets to write a word of it, which is what keeps
 * a near miss from becoming a place to smuggle in invented physics.
 */
export type Routing<C extends BaseIntentContext> =
  | { kind: 'run'; intent: Intent<C>; params: IntentParams }
  | { kind: 'nearest'; intent: Intent<C> }
  | { kind: 'none' }

export class Assistant<C extends BaseIntentContext> {
  readonly history: Transcript[] = []

  /**
   * How a question becomes a routing. Defaults to the local pattern router;
   * `installRouterFactory` swaps in a hosted model without any module having to
   * know. May be async - a hosted call is a round trip.
   *
   * Whatever it returns names one of the REGISTERED intents and carries only
   * params those intents declared. Both are enforced at the boundary rather
   * than trusted here.
   */
  router: (question: string) => Routing<C> | Promise<Routing<C>>

  /** True when this module's experiments run off the main thread. */
  offThread = false

  constructor(
    private readonly intents: Intent<C>[],
    /** Builds the context an intent runs against, closing over the module's sim. */
    private readonly makeContext: (question: string, track: TrackRun, params: IntentParams) => C,
  ) {
    this.router = installedFactory
      ? (installedFactory as RouterFactory)(this.intents)
      : (question) => routeLocally(this.intents, question)
  }

  /**
   * Forget the transcript.
   *
   * Called when the signed-in student changes. A fab machine is shared - that
   * is the whole reason the guest door exists - and a sign-out that left the
   * previous student's questions sitting in the panel would be a sign-out in
   * name only.
   */
  reset(): void {
    this.history.length = 0
  }

  /**
   * Questions to offer as chips before the student has asked anything.
   *
   * The LABEL is localised by intent id; the QUESTION underneath stays the
   * English example, because it is what the trigger patterns match. A chip
   * shows the student their language and speaks English to the router.
   */
  suggestions(): Array<{ label: string; question: string; summary: string }> {
    return this.intents.map((i) => ({
      label: tOr(`assistant.example.${i.id}`, i.example),
      question: i.example,
      summary: i.summary,
    }))
  }

  async ask(question: string, onProgress?: (p: AskProgress) => void): Promise<Transcript> {
    this.history.push({ role: 'student', text: question })

    const routing = await this.router(question)
    if (routing.kind !== 'run') {
      const turn =
        routing.kind === 'nearest'
          ? this.nearestResponse(question, routing.intent)
          : this.helpResponse(question)
      this.history.push(turn)
      return turn
    }
    const intent = routing.intent

    // The intent declares how many conditions it expects, so the bar reads
    // "2 of 5" rather than always "N of N". If an intent queues more than it
    // declared, the total widens rather than the bar pinning at full.
    let done = 0
    let total = intent.expectedRuns

    const track: TrackRun = async (label, exec) => {
      total = Math.max(total, done + 1)
      onProgress?.({ done, total, label })
      const result = await exec()
      done++
      onProgress?.({ done, total, label })
      return result
    }

    try {
      const report = await intent.execute(this.makeContext(question, track, routing.params))
      const turn: Transcript = { role: 'assistant', text: report.understood, report }
      this.history.push(turn)
      return turn
    } catch (err) {
      const turn: Transcript = {
        role: 'assistant',
        text:
          'That experiment did not complete: ' +
          (err instanceof Error ? err.message : String(err)) +
          '. The tool is untouched.',
      }
      this.history.push(turn)
      return turn
    }
  }

  /** The full menu, in the shape the report renders findings in. */
  private catalogue(): string[] {
    return this.intents.map((i) => `${i.example} - ${i.summary}`)
  }

  /**
   * No intent matched and none was close. Rather than guess, say so and show
   * what CAN be asked - an assistant that invents an answer here would
   * undermine the one property this whole design exists to guarantee.
   */
  private helpResponse(question: string): Transcript {
    return {
      role: 'assistant',
      text: t('assistant.CannotRun', { question: question.trim() }),
      report: {
        understood: t('assistant.NoMatch'),
        hypothesis: '',
        design: '',
        findings: this.catalogue(),
        readouts: [],
        charts: [],
        runs: [],
        followUp: t('assistant.NoMatchFollowUp'),
      },
    }
  }

  /**
   * No intent matched, but one is close. This is the common case and it used to
   * be indistinguishable from the one above.
   *
   * Most of these are not really unanswerable questions - they are vocabulary
   * gaps. A student says "the edges look burnt"; the experiment that answers it
   * is filed under a centre-to-edge signature, and the catalogue is written in
   * the words a process engineer would use rather than the words a student
   * arrives with. Naming the nearest experiment is translation, not an answer.
   *
   * It is OFFERED rather than run. Running something adjacent to what was asked
   * and presenting the result as the answer is the failure this design exists
   * to prevent; putting it behind a button the student presses, next to the
   * full menu, is not.
   */
  private nearestResponse(question: string, intent: Intent<C>): Transcript {
    return {
      role: 'assistant',
      text: t('assistant.NearestLead', {
        question: question.trim(),
        example: intent.example,
        summary: intent.summary,
      }),
      report: {
        understood: t('assistant.NearestMatch'),
        hypothesis: '',
        design: '',
        findings: this.catalogue(),
        readouts: [],
        charts: [],
        runs: [],
        offer: { label: t('assistant.RunNearest'), question: intent.example },
        followUp: t('assistant.NearestFollowUp'),
      },
    }
  }
}

/**
 * Route a question by counting trigger hits. Deliberately simple and local: it
 * runs offline in a classroom with no network, and every routing decision is
 * inspectable.
 */
export function routeAmong<C extends BaseIntentContext>(
  intents: Intent<C>[],
  question: string,
): Intent<C> | null {
  let best: { intent: Intent<C>; score: number } | null = null
  for (const intent of intents) {
    let score = 0
    for (const t of intent.triggers) if (t.test(question)) score++
    if (score > 0 && (!best || score > best.score)) best = { intent, score }
  }
  return best?.intent ?? null
}

/**
 * Words too common to mean anything about which experiment was wanted. Short
 * tokens are dropped by length in `contentWords`, so this only needs the longer
 * ones that would otherwise match every intent equally.
 */
const STOPWORDS = new Set([
  'the', 'and', 'are', 'was', 'were', 'been', 'being', 'have', 'has', 'had',
  'why', 'what', 'how', 'when', 'which', 'where', 'who', 'whom', 'whose',
  'does', 'did', 'can', 'could', 'would', 'should', 'this', 'that', 'these',
  'those', 'there', 'here', 'you', 'your', 'not', 'but', 'for', 'with', 'from',
  'into', 'get', 'got', 'any', 'all', 'about', 'just', 'still', 'now', 'then',
  'than', 'they', 'them', 'its',
])

const contentWords = (s: string): string[] =>
  (s.toLowerCase().match(/[a-z]{3,}/g) ?? []).filter((w) => !STOPWORDS.has(w))

/**
 * The closest experiment to a question that matched no trigger.
 *
 * Deliberately dumb: shared content words between the question and an intent's
 * own example and summary. It cannot read a sentence and is not trying to - it
 * exists so that the offline classroom gets the same near-miss offer as the
 * hosted one, in the cases where the student happened to use a word the
 * catalogue also uses.
 *
 * Two shared words minimum, and a word only counts if it distinguishes. The
 * second half of that matters more than it sounds: "wafer" is in half the
 * intents in this app, so matching on it says nothing about which experiment
 * was wanted, and a stopword list would have to be extended by hand every time
 * a module was added. Words carried by most of the catalogue are dropped
 * instead - the list maintains itself against whatever intents exist.
 *
 * Offering an experiment on the strength of a coincidence would be worse than
 * the honest refusal it replaced. Nothing here runs on its own regardless; the
 * result is only ever shown as an offer.
 */
export function nearestAmong<C extends BaseIntentContext>(
  intents: Intent<C>[],
  question: string,
): Intent<C> | null {
  const asked = new Set(contentWords(question))
  if (!asked.size || !intents.length) return null

  const vocabularies = intents.map((i) => new Set(contentWords(`${i.example} ${i.summary}`)))

  // Words this catalogue uses everywhere carry no signal about which of its
  // experiments was meant.
  const common = new Set<string>()
  for (const w of asked) {
    if (vocabularies.filter((v) => v.has(w)).length * 2 > intents.length) common.add(w)
  }

  let best: { intent: Intent<C>; score: number } | null = null
  for (let i = 0; i < intents.length; i++) {
    let score = 0
    for (const w of asked) if (!common.has(w) && vocabularies[i].has(w)) score++
    if (!best || score > best.score) best = { intent: intents[i], score }
  }
  return best && best.score >= 2 ? best.intent : null
}

/**
 * The whole local routing decision: match a trigger and read any number the
 * student typed, or fall back to naming the nearest experiment.
 *
 * This is what runs with no network, and it is a real fallback rather than a
 * degraded one - the same three outcomes as the hosted router, decided from the
 * text alone.
 */
export function routeLocally<C extends BaseIntentContext>(
  intents: Intent<C>[],
  question: string,
): Routing<C> {
  const intent = routeAmong(intents, question)
  if (intent) return { kind: 'run', intent, params: scanParams(intent.params, question) }
  const near = nearestAmong(intents, question)
  return near ? { kind: 'nearest', intent: near } : { kind: 'none' }
}

/**
 * A replacement for the local router, applied to every assistant built after it
 * is installed.
 *
 * The six modules each build their assistant lazily, the first time a student
 * opens the panel on that tool. Rather than thread a provider through six
 * factories that have no business knowing about one, the choice is made once at
 * boot and picked up here. Install null to go back to pattern routing, which is
 * what the app does when no hosted provider is configured.
 */
export type RouterFactory = <C extends BaseIntentContext>(
  intents: Intent<C>[],
) => (question: string) => Routing<C> | Promise<Routing<C>>

let installedFactory: RouterFactory | null = null

export function installRouterFactory(factory: RouterFactory | null): void {
  installedFactory = factory
}
